import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { Provider, TeamsThemeStylesProps, ThemePrepared, teamsTheme, teamsDarkTheme } from "@fluentui/react-northstar";
import * as microsoftTeams from "@microsoft/teams-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createBrowserHistory } from "history";

import { AuthProvider } from "./contexts/authContext";
import { AppProvider } from "./contexts/appContext";
import { getPersistOptions, queryClient } from "./queries/queryClient";
import App from "./App";
import "./index.css";

//appliction insights
const browserHistory = createBrowserHistory();
const reactPlugin = new ReactPlugin();
const appInsights = new ApplicationInsights({
    config: {
        connectionString: import.meta.env.VITE_AI_CONNECTION_STRING,
        extensions: [reactPlugin],
        extensionConfig: {
            [reactPlugin.identifier]: { history: browserHistory }
        }
    }
});
appInsights.loadAppInsights();

// Teams dialogs are a separate browser context sharing the storage with the main window,
// they may read the persisted cache but must never write it
const isDialog = window.location.href.indexOf("/modal/") > -1;
const persistOptions = getPersistOptions(isDialog);

function render(theme: ThemePrepared<TeamsThemeStylesProps>, isDarkTheme: boolean, bgColor: string, color: string) {
    ReactDOM.render(
        <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
            <Provider theme={theme}>
                <AppProvider isDarkTheme={isDarkTheme} backgroundColor={bgColor} color={color}>
                    <BrowserRouter>
                        <AuthProvider>
                            <App />
                        </AuthProvider>
                    </BrowserRouter>
                </AppProvider>
            </Provider>
        </PersistQueryClientProvider>
        , document.getElementById("root")
    );
}

function isLightTheme(themeName?: string) {
    return !themeName || themeName === "default" ? true : false;
}

function getThemeByName(themeName?: string) {
    return isLightTheme(themeName) ? teamsTheme : teamsDarkTheme;
}

function getBgColorByThemeName(themeName?: string) {
    return isLightTheme(themeName) ? "#FFFFFF" : "#2D2C2C";
}

function getColorByThemeName(themeName?: string) {
    return isLightTheme(themeName) ? "#252423" : "#FFFFFF";
}

function updateTheme(themeName: string) {
    render(getThemeByName(themeName), !isLightTheme(themeName), getBgColorByThemeName(themeName), getColorByThemeName(themeName));
}

microsoftTeams.app.initialize();
microsoftTeams.app.getContext().then((context: microsoftTeams.app.Context) => {
    updateTheme(context.app.theme ?? "default");
});

microsoftTeams.app.registerOnThemeChangeHandler(updateTheme)
